import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import extractText from 'flarum/common/utils/extractText';

import { N8N_TAG_SLUG, seesN8nTag } from './utils/routeState';
import { CARD_SELECTOR, GATE_CTA_SELECTOR, cardSlug, originalHref } from './utils/landingCards';

/**
 * Lo de la landing que depende de la SESION, en un solo sitio.
 *
 * La landing es HTML crudo servido en `custom_header`, hermano de `#app` y fuera del arbol de
 * Mithril (ver `forum.blade.php`): no puede preguntar por la sesion ni recibir props. Todo lo
 * que tenga que cambiar segun quien mira se hace desde aqui, por DOM, en `IndexPage.oncreate`
 * — el primer punto con la store poblada, y que vuelve a correr al regresar a la home por el
 * SPA, que es cuando la sesion puede haber cambiado.
 *
 * Dos trabajos, la misma regla:
 *
 *   (1) LA TARJETA DE CATEGORIA n8n, para que el clic no acabe en un 404 (T10).
 *   (2) LOS CTAS DE LAS DOS PUERTAS, para que no le ofrezcan crear una cuenta a quien ya la
 *       tiene (PKG-FORO-PRODUCT-GATES · decision A).
 *
 * Los dos son IDEMPOTENTES Y REVERSIBLES: guardan el valor original en un `data-` antes de
 * pisarlo, de modo que volver al estado anterior (cerrar sesion) restaura la landing tal como
 * la escribio el HTML. Sin esa copia, una tarjeta ya reescrita perderia su slug y un CTA ya
 * reescrito perderia su texto de alta.
 */

/**
 * (10) Que ninguna tarjeta de la landing lleve a un 404 (T10).
 *
 * MEDIDO EN VIVO antes de tocar nada (comunidad.umbralbim.io, anonimo, 1078px y 390px):
 * las 7 tarjetas reciben el clic — `pointer-events:auto`, y un hit-test en 3 puntos de cada
 * una devuelve siempre la propia tarjeta, asi que NO hay ni escudo de `pointer-events` ni
 * overlay ni intercepcion del SPA. Lo que si falla es el destino de UNA de ellas:
 *
 *     GET /t/bim-coordinacion  200      GET /t/encuentros  200
 *     GET /t/datos-tableros    200      GET /t/recursos    200
 *     GET /t/ia-agentes        200      GET /t/n8n-chile   404   <-- aqui
 *     GET /t/obra-terreno      200
 *
 * `n8n-chile` es `is_restricted`: solo responde 200 a quien tenga `tag7.viewForum`. Para
 * cualquier otra persona el clic navega a una pagina de error, que es exactamente «hago clic
 * y no pasa nada util».
 *
 * EL PATRON YA EXISTIA, la landing se habia quedado fuera: `addHeaderNav` manda al anonimo a
 * la pagina publica del grupo (D7, `/n8n-chile`) y solo al miembro a la categoria
 * (runbook §13.4). Aqui se aplica la MISMA regla a la tarjeta, con la MISMA sonda
 * (`seesN8nTag()`, que mira la visibilidad efectiva de la etiqueta en el payload de esta
 * sesion — la condicion que decide el 200 vs 404).
 *
 * NOTA (PKG-FORO-PRODUCT-GATES · decision B): la tarjeta «Encuentros» NO se toca aqui aunque
 * ahora `/t/encuentros` este tras un muro para el anonimo. Vive fuera de `#app`, asi que su
 * clic es una navegacion completa del navegador y la atiende el servidor
 * (`Forum\MeetupsFeedLoginWall`), que responde 302 al muro. Reescribir aqui el destino seria
 * una segunda copia de esa regla, y la de arriba es la unica que manda.
 */
function retargetCards() {
  const cards = document.querySelectorAll<HTMLAnchorElement>(CARD_SELECTOR);

  if (!cards.length) return;

  const memberPath = app.route('tag', { tags: N8N_TAG_SLUG });
  const publicPath = app.route('umbral-comunidad.n8nChile');
  const wanted = seesN8nTag() ? memberPath : publicPath;

  cards.forEach((card) => {
    if (cardSlug(card) !== N8N_TAG_SLUG) return;

    // Se guarda el destino original ANTES de pisarlo: es lo que deja que esta funcion sea
    // idempotente y reversible (cerrar sesion tiene que devolver la tarjeta a D7).
    card.setAttribute('data-cm-href', originalHref(card));

    if (card.getAttribute('href') !== wanted) card.setAttribute('href', wanted);
  });
}

/**
 * (A) «Crear cuenta» -> «Entrar» cuando ya hay sesion.
 *
 * EL BUG QUE CIERRA, reportado en vivo: las dos puertas de la landing ofrecen «Crear cuenta» y
 * «Crear cuenta Umbral» A TODO EL MUNDO, con sesion o sin ella. Quien ya entro ve su propia
 * comunidad invitandole a darse de alta otra vez, y el clic le lleva a `/registro` — una
 * pantalla que para el no tiene nada que hacer.
 *
 * NO BASTA CON CAMBIAR EL TEXTO NI EL `href`. El propio `custom_header` trae un listener
 * delegado sobre `document` que intercepta CUALQUIER `a[data-cm-signup]`, hace `preventDefault`
 * y navega a `window.__cmRegisterPath` sin mirar la sesion — y ese puente lo publica
 * `addRegisterLink` tambien para quien ya entro, a proposito. O sea: dejar el atributo puesto
 * significa que «Entrar» seguiria aterrizando en `/registro`. Por eso el atributo se MUEVE:
 *
 *     sin sesion   `data-cm-signup="1"`   -> lo intercepta la landing, va al alta
 *     con sesion   `data-cm-entrar="1"`   -> la landing lo ignora, manda el `href` de verdad
 *
 * Y por eso tambien el intercambio es simetrico: cerrar sesion devuelve `data-cm-signup` y el
 * texto original, sin recargar nada.
 *
 * LOS DESTINOS los fija la decision A del paquete:
 *
 *     puerta Umbral (`.cm-gate.cm-a`)  -> el feed limpio, `/discusiones`
 *     puerta n8n    (`.cm-gate.cm-b`)  -> `/t/n8n-chile` si la sesion ve la categoria, y si no
 *                                         la pagina publica del grupo `/n8n-chile`, que es
 *                                         donde estan «Solicitar acceso» e «Ingresar codigo».
 *
 * El destino de n8n usa la MISMA sonda que la tarjeta de arriba (`seesN8nTag()`), no la
 * pertenencia al grupo: es la que decide el 200 vs 404 de la categoria.
 */
function retargetGateCtas() {
  const ctas = document.querySelectorAll<HTMLAnchorElement>(GATE_CTA_SELECTOR);

  if (!ctas.length) return;

  const signedIn = !!app.session.user;
  const enterLabel = extractText(app.translator.trans('umbral-comunidad.forum.landing.cta_enter'));
  const n8nPath = seesN8nTag() ? app.route('tag', { tags: N8N_TAG_SLUG }) : app.route('umbral-comunidad.n8nChile');
  const umbralPath = app.route('umbral-comunidad.discussions');

  ctas.forEach((cta) => {
    // Copia de seguridad del estado que escribio el HTML. Se toma UNA sola vez —- si ya
    // existe, el CTA ya paso por aqui y su texto actual es el nuestro, no el original.
    if (!cta.hasAttribute('data-cm-label')) cta.setAttribute('data-cm-label', cta.textContent || '');
    if (!cta.hasAttribute('data-cm-href')) cta.setAttribute('data-cm-href', cta.getAttribute('href') || '/');

    if (!signedIn) {
      cta.textContent = cta.getAttribute('data-cm-label') || cta.textContent;
      cta.setAttribute('href', cta.getAttribute('data-cm-href') || '/');
      cta.setAttribute('data-cm-signup', '1');
      cta.removeAttribute('data-cm-entrar');

      return;
    }

    // `.cm-gate.cm-b` es la puerta n8n y `.cm-a` la de Umbral, tal como las escribe el HTML de
    // la landing. Se pregunta por el ANCESTRO y no por la clase del propio boton porque el
    // boton solo trae clases de pintado (`cm-primary` / `cm-n8n`), que son estilo y no destino.
    const gate = cta.closest('.cm-gate');
    const wanted = gate && gate.classList.contains('cm-b') ? n8nPath : umbralPath;

    cta.textContent = enterLabel;
    cta.setAttribute('href', wanted);
    cta.removeAttribute('data-cm-signup');
    cta.setAttribute('data-cm-entrar', '1');
  });
}

function retarget() {
  retargetCards();
  retargetGateCtas();
}

export default function retargetLandingLinks() {
  extend(IndexPage.prototype, 'oncreate', retarget);
}
